import { useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { SORTABLE_COLUMNS } from '../api/types'
import type { PerilRecord, PerilSearchResponse, SearchBy, SortState } from '../api/types'
import {
  bushfireRisk,
  floodScoreRisk,
  formatAri,
  formatBushfireLevel,
  formatElevation,
  formatText,
  formatYesNo,
} from '../lib/peril'
import type { RiskStep } from '../lib/peril'
import { Button, Callout, StatusRow } from './ui'

interface Column {
  key: keyof PerilRecord
  label: string
  numeric?: boolean
  propertyOnly?: boolean
  /** Long values are clipped, so the full text goes on the title attribute. */
  truncate?: boolean
  format?: (item: PerilRecord) => string
  /** Cells the legacy grid fills with a traffic light colour. */
  risk?: (item: PerilRecord) => RiskStep | null
}

/** Column order matches the legacy K2 grid exactly. */
const COLUMNS: Column[] = [
  { key: 'gnafPid', label: 'GNAF PID', truncate: true },
  { key: 'perilAddress', label: 'Peril Address', truncate: true },
  {
    key: 'propertyStreetAddress',
    label: 'Property Street Address',
    propertyOnly: true,
    truncate: true,
  },
  {
    key: 'floodScore',
    label: 'Flood Score',
    numeric: true,
    risk: (item) => floodScoreRisk(item.floodScore),
  },
  {
    key: 'floodAriGl',
    label: 'Flood ARI GL',
    numeric: true,
    format: (item) => formatAri(item.floodAriGl),
  },
  {
    key: 'floodAriGl1m',
    label: 'Flood ARI GL 1M',
    numeric: true,
    format: (item) => formatAri(item.floodAriGl1m),
  },
  {
    key: 'floodAriGl2m',
    label: 'Flood ARI GL 2M',
    numeric: true,
    format: (item) => formatAri(item.floodAriGl2m),
  },
  {
    key: 'elevation',
    label: 'Elevation',
    numeric: true,
    format: (item) => formatElevation(item.elevation),
  },
  { key: 'arpcWindZone', label: 'ARPC Wind Zone' },
  { key: 'arpcWindBand', label: 'ARPC Wind Band' },
  { key: 'arpcFloodBand', label: 'ARPC Flood Band' },
  { key: 'arpcSurgeBand', label: 'ARPC Surge Band' },
  {
    key: 'bushfireAttackLevel',
    label: 'B/Fire Attack Level',
    numeric: true,
    format: (item) => formatBushfireLevel(item.bushfireAttackLevel),
  },
  {
    key: 'bushfireAttackLevelCategory',
    label: 'B/Fire Attack Level Category',
    // Coloured by the category name, which is what carries the ordering. The
    // numeric level is a decimal measure and runs well past 5, so it is not an
    // index into the ramp. See bushfireRisk.
    risk: (item) => bushfireRisk(item.bushfireAttackLevelCategory),
  },
  { key: 'source', label: 'Source' },
  { key: 'primaryRef', label: 'Primary Ref', format: (item) => formatYesNo(item.primaryRef) },
]

interface ResultsCardProps {
  searchBy: SearchBy
  result: PerilSearchResponse | null
  isLoading: boolean
  error: string | null
  selectedRef: string | null
  sort: SortState
  onSort: (column: keyof PerilRecord) => void
  onSelect: (recordRef: string) => void
  onViewDetails: () => void
  /** Opens one record straight away, without selecting it first. */
  onOpenDetails: (recordRef: string) => void
  /** Abandons a search that is still running. */
  onCancel: () => void
  onRetry: () => void
  onPage: (page: number) => void
}

export function ResultsCard({
  searchBy,
  result,
  isLoading,
  error,
  selectedRef,
  sort,
  onSort,
  onSelect,
  onViewDetails,
  onOpenDetails,
  onCancel,
  onRetry,
  onPage,
}: ResultsCardProps) {
  const columns = COLUMNS.filter((column) => !column.propertyOnly || searchBy === 'property')
  const items = result?.items ?? []
  /**
   * False until a search has actually run, which is not the same as a search
   * returning nothing.
   *
   * "No items to display" sat under the empty grid from the moment the page
   * loaded, so a fast search that legitimately matched nothing was
   * indistinguishable from a page nobody had searched on yet, and read as a
   * glitch. `result` is null before the first search and is cleared again by
   * Clear Values, so it is exactly the right signal.
   */
  const hasSearched = result !== null
  const page = result?.page ?? 1
  const totalPages = result?.totalPages ?? null
  const totalItems = result?.totalItems ?? null

  // Sixteen columns do not always fit, even on a wide monitor, so the grid can
  // take the whole screen. Local state: nothing outside this card cares.
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    if (!isFullScreen) return
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') setIsFullScreen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFullScreen])

  return (
    <section
      className={`card card--accent${isFullScreen ? ' card--fullscreen' : ''}`}
      aria-label="Search results"
    >
      <div className="card__header">
        <h2 className="card__title">Search Results</h2>
        <div className="button-group">
          <Button
            variant="secondary"
            size="small"
            onClick={() => setIsFullScreen((current) => !current)}
            aria-pressed={isFullScreen}
          >
            {isFullScreen ? 'Exit full screen' : 'Full screen'}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={onViewDetails}
            disabled={!selectedRef || isLoading}
          >
            View Details
          </Button>
        </div>
      </div>

      {error ? (
        <div className="card__body">
          <Callout onRetry={onRetry}>{error}</Callout>
        </div>
      ) : isLoading ? (
        <StatusRow onCancel={onCancel}>Searching the peril database</StatusRow>
      ) : (
        <>
          <div className="grid-scroll">
            <table className="grid">
              <caption className="visually-hidden">
                Peril records matching your search. Select a row with the space bar, or open it with
                Enter or a double click. Column headers sort the whole result set, not just this
                page. Flood Score and B/Fire Attack Level Category cells are shaded by severity,
                from least risk to riskiest.
              </caption>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <HeaderCell
                      key={column.key}
                      column={column}
                      sort={sort}
                      onSort={onSort}
                      disabled={items.length === 0}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <Row
                    key={item.recordRef}
                    item={item}
                    columns={columns}
                    isSelected={item.recordRef === selectedRef}
                    onSelect={onSelect}
                    onOpen={onOpenDetails}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 ? (
            <p className="grid__empty">
              {hasSearched ? 'No items to display' : 'Submit your search'}
            </p>
          ) : null}
        </>
      )}

      <div className="card__footer">
        <span className="result-count">
          {/* Blank rather than "No records" before a search: there are no
              records because nothing has been asked for yet. */}
          {hasSearched ? describeCount(items.length, page, totalItems, result?.pageSize ?? 30) : ''}
        </span>
        <div className="paging">
          <Button
            variant="secondary"
            size="icon"
            aria-label="First page"
            disabled={page <= 1 || isLoading}
            onClick={() => onPage(1)}
          >
            {'|<'}
          </Button>
          <Button
            variant="secondary"
            size="icon"
            aria-label="Previous page"
            disabled={page <= 1 || isLoading}
            onClick={() => onPage(page - 1)}
          >
            {'<'}
          </Button>
          <span className="paging__status">
            {totalPages ? `Page ${page} of ${totalPages}` : `Page ${page}`}
          </span>
          <Button
            variant="secondary"
            size="icon"
            aria-label="Next page"
            disabled={!result?.hasMore || isLoading}
            onClick={() => onPage(page + 1)}
          >
            {'>'}
          </Button>
        </div>
      </div>
    </section>
  )
}

interface HeaderCellProps {
  column: Column
  sort: SortState
  onSort: (column: keyof PerilRecord) => void
  disabled: boolean
}

/**
 * A column header, sortable where the legacy grid sorts.
 *
 * Primary Ref is not sortable, matching production: it is a yes or no flag over
 * a set that is almost entirely yes.
 */
function HeaderCell({ column, sort, onSort, disabled }: HeaderCellProps) {
  const sortable = SORTABLE_COLUMNS.has(column.key)
  const active = sort.sortBy === column.key
  const ariaSort = active ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'

  if (!sortable) {
    return <th scope="col">{column.label}</th>
  }

  return (
    <th scope="col" aria-sort={ariaSort}>
      <button
        type="button"
        className={`grid__sort${active ? ' grid__sort--active' : ''}`}
        onClick={() => onSort(column.key)}
        disabled={disabled}
      >
        <span>{column.label}</span>
        {/* Character arrows rather than an icon font, so the indicator survives
            a blocked asset and prints. aria-hidden because aria-sort on the
            header already carries this to a screen reader. */}
        <span className="grid__sort-indicator" aria-hidden="true">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}

interface RowProps {
  item: PerilRecord
  columns: Column[]
  isSelected: boolean
  onSelect: (recordRef: string) => void
  onOpen: (recordRef: string) => void
}

function Row({ item, columns, isSelected, onSelect, onOpen }: RowProps) {
  // Enter is the keyboard's double click: it opens the record. Space selects,
  // which is what the View Details button then acts on.
  function handleKeyDown(event: KeyboardEvent<HTMLTableRowElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      onOpen(item.recordRef)
      return
    }
    if (event.key === ' ') {
      event.preventDefault()
      onSelect(item.recordRef)
    }
  }

  return (
    <tr
      aria-selected={isSelected}
      tabIndex={0}
      onClick={() => onSelect(item.recordRef)}
      onDoubleClick={() => onOpen(item.recordRef)}
      onKeyDown={handleKeyDown}
    >
      {columns.map((column) => (
        <Cell key={column.key} column={column} item={item} />
      ))}
    </tr>
  )
}

function Cell({ column, item }: { column: Column; item: PerilRecord }) {
  const text = column.format ? column.format(item) : formatText(item[column.key] as string | null)
  const risk = column.risk?.(item) ?? null

  const classes = [
    column.numeric ? 'is-numeric' : '',
    risk ? 'is-risk' : '',
    column.truncate ? 'is-truncate' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <td className={classes || undefined} title={column.truncate && text ? text : undefined}>
      {risk ? <span className={`risk-chip risk-chip--${risk}`}>{text}</span> : text}
    </td>
  )
}

function describeCount(
  shown: number,
  page: number,
  totalItems: number | null,
  pageSize: number,
): string {
  if (shown === 0) return 'No records'
  const from = (page - 1) * pageSize + 1
  const to = from + shown - 1
  if (totalItems === null) return `Showing ${from} to ${to}`
  return `Showing ${from} to ${to} of ${totalItems}`
}
